import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  ScrollView,
  ActivityIndicator,
  SafeAreaView,
  Dimensions,
  Alert,
  Animated,
  TextInput,
  Modal,
  Linking,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../navigation";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import Header from "../components/Header";
import {
  getSchoolOpportunities,
  getSchoolAdminStats,
  removeOpportunityFromSchool,
  updateSchoolOpportunityVisibility,
  addOpportunityToSchool,
  searchOpportunitiesForSchool,
  createOpportunityForSchool,
} from "../services/parseService";

const { width } = Dimensions.get("window");
const isDesktop = width >= 1024;

// Modern color palette
const getThemedColors = (theme: 'light' | 'dark') => ({
  // Backgrounds
  background: theme === "dark" ? "#0F0F12" : "#F8FAFC",
  backgroundSecondary: theme === "dark" ? "#18181B" : "#FFFFFF",
  card: theme === "dark" ? "#1C1C21" : "#FFFFFF",
  cardHover: theme === "dark" ? "#232329" : "#F8FAFC",
  
  // Text
  text: theme === "dark" ? "#FAFAFA" : "#0F172A",
  textSecondary: theme === "dark" ? "#A1A1AA" : "#64748B",
  textMuted: theme === "dark" ? "#71717A" : "#94A3B8",
  
  // Accents
  primary: "#3B82F6",
  primaryLight: theme === "dark" ? "rgba(59, 130, 246, 0.15)" : "rgba(59, 130, 246, 0.08)",
  primaryGlow: theme === "dark" ? "rgba(59, 130, 246, 0.4)" : "rgba(59, 130, 246, 0.2)",
  
  success: "#10B981",
  successLight: theme === "dark" ? "rgba(16, 185, 129, 0.15)" : "rgba(16, 185, 129, 0.08)",
  
  warning: "#F59E0B",
  warningLight: theme === "dark" ? "rgba(245, 158, 11, 0.15)" : "rgba(245, 158, 11, 0.08)",
  
  error: "#EF4444",
  errorLight: theme === "dark" ? "rgba(239, 68, 68, 0.15)" : "rgba(239, 68, 68, 0.08)",
  
  purple: "#8B5CF6",
  purpleLight: theme === "dark" ? "rgba(139, 92, 246, 0.15)" : "rgba(139, 92, 246, 0.08)",
  
  // Borders & Dividers
  border: theme === "dark" ? "#27272A" : "#E2E8F0",
  borderLight: theme === "dark" ? "#3F3F46" : "#F1F5F9",
  
  // Sidebar
  sidebarBg: theme === "dark" ? "#18181B" : "#FFFFFF",
  sidebarBorder: theme === "dark" ? "#27272A" : "#E2E8F0",
  
  // Interactive
  buttonBg: theme === "dark" ? "#27272A" : "#F1F5F9",
  buttonHover: theme === "dark" ? "#3F3F46" : "#E2E8F0",
});

type DashboardSection = "overview" | "opportunities" | "browse" | "settings";

interface DashboardStats {
  totalOpportunities: number;
  activeOpportunities: number;
  totalTeacherSignups: number;
}

interface SchoolOpportunityData {
  schoolOpportunity: any;
  opportunity: any;
  teacherCount: number;
  teachers: Array<{ name: string; roomNumber?: string }>;
}

const SchoolAdminDashboard: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const colors = getThemedColors(theme);
  
  const [activeSection, setActiveSection] = useState<DashboardSection>("overview");
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    totalOpportunities: 0,
    activeOpportunities: 0,
    totalTeacherSignups: 0,
  });
  const [opportunities, setOpportunities] = useState<SchoolOpportunityData[]>([]);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<SchoolOpportunityData | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [expandedOpps, setExpandedOpps] = useState<Set<string>>(new Set());

  // Create opportunity state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: "",
    description: "",
    organization: "",
    type: "Program",
    location: "",
    website: "",
    eligibility: "",
    cost: "",
    startDate: "",
    endDate: "",
    applicationCloseDate: "",
  });
  const [isSchoolOnly, setIsSchoolOnly] = useState(true);

  const themeIconRotation = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // User/school info
  const isSchoolAdmin = currentUser && currentUser.get('userType') === 'school_admin';
  const schoolPointer = currentUser?.get('school');
  const schoolId = schoolPointer?.id;
  const schoolName = schoolPointer?.get ? (schoolPointer.get('name') || 'Your School') : 'Your School';
  const schoolSlug = schoolPointer?.get ? (schoolPointer.get('slug') || '') : '';
  const schoolWebsite = schoolPointer?.get ? (schoolPointer.get('website') || '') : '';
  const adminEmail = currentUser?.get('email') || '';
  const adminName = currentUser?.get('name') || adminEmail.split('@')[0] || 'Admin';

  useEffect(() => {
    if (isSchoolAdmin && schoolId) {
      loadDashboardData();
    } else if (isSchoolAdmin && !schoolId) {
      setError('No school assigned to your account. Please contact support.');
      setIsLoading(false);
    } else if (currentUser) {
      setIsLoading(false);
    }
  }, [isSchoolAdmin, schoolId, currentUser]);

  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 400,
      useNativeDriver: true,
    }).start();
  }, []);

  useEffect(() => {
    Animated.sequence([
      Animated.timing(themeIconRotation, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(themeIconRotation, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      }),
    ]).start();
  }, [theme]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      if (!schoolId) {
        setError('Unable to load data. Please try logging in again.');
        setIsLoading(false);
        return;
      }

      const [stats, schoolOpps] = await Promise.all([
        getSchoolAdminStats(schoolId),
        getSchoolOpportunities(schoolId),
      ]);

      setDashboardStats(stats);
      setOpportunities(schoolOpps);
    } catch (err: any) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewOpportunity = (item: SchoolOpportunityData) => {
    setSelectedOpportunity(item);
    setShowDetailModal(true);
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'Not set';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const results = await searchOpportunitiesForSchool(searchTerm);
      // Filter out opportunities already added to this school
      const existingIds = new Set(opportunities.map(o => o.opportunity?.id).filter(Boolean));
      setSearchResults(results.filter((opp: any) => !existingIds.has(opp.id)));
    } catch (error) {
      console.error('Error searching:', error);
      Alert.alert('Error', 'Failed to search opportunities');
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddOpportunity = async (opportunityId: string) => {
    try {
      if (!schoolId || !currentUser?.id) return;

      await addOpportunityToSchool(schoolId, opportunityId, currentUser.id);
      Alert.alert('Success', 'Opportunity added to your school');
      setSearchTerm('');
      setSearchResults([]);
      loadDashboardData();
    } catch (error: any) {
      console.error('Error adding opportunity:', error);
      Alert.alert('Error', error.message || 'Failed to add opportunity');
    }
  };

  const handleRemoveOpportunity = async (schoolOpportunityId: string) => {
    Alert.alert(
      'Remove Opportunity',
      'Are you sure you want to remove this opportunity from your school?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            try {
              if (!currentUser?.id) return;
              await removeOpportunityFromSchool(schoolOpportunityId, currentUser.id);
              loadDashboardData();
            } catch (error) {
              console.error('Error removing opportunity:', error);
              Alert.alert('Error', 'Failed to remove opportunity');
            }
          },
        },
      ]
    );
  };

  const handleToggleVisibility = async (schoolOpportunityId: string, currentVisibility: boolean) => {
    try {
      if (!currentUser?.id) return;
      await updateSchoolOpportunityVisibility(schoolOpportunityId, !currentVisibility, currentUser.id);
      loadDashboardData();
    } catch (error) {
      console.error('Error toggling visibility:', error);
      Alert.alert('Error', 'Failed to update visibility');
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      }
      // Show confirmation
      if (Platform.OS === 'web') {
        window.alert('Link copied to clipboard');
      } else {
        Alert.alert('Copied', 'Link copied to clipboard');
      }
    } catch {
      // Fallback: just alert the URL
      if (Platform.OS === 'web') {
        window.alert(`Copy this link: ${text}`);
      } else {
        Alert.alert('Link', text);
      }
    }
  };

  const handleCreateOpportunity = async () => {
    if (!currentUser?.id || !schoolId) return;

    if (!createForm.title.trim() || !createForm.description.trim() || !createForm.organization.trim()) {
      Alert.alert("Error", "Please fill in title, description, and organization");
      return;
    }

    try {
      setIsCreating(true);
      await createOpportunityForSchool(
        schoolId,
        {
          title: createForm.title.trim(),
          description: createForm.description.trim(),
          organization: createForm.organization.trim(),
          type: createForm.type,
          location: createForm.location.trim(),
          website: createForm.website.trim() || undefined,
          eligibility: createForm.eligibility.trim() || undefined,
          cost: createForm.cost.trim() || undefined,
          startDate: createForm.startDate || undefined,
          endDate: createForm.endDate || undefined,
          applicationCloseDate: createForm.applicationCloseDate || undefined,
        },
        currentUser.id,
        isSchoolOnly
      );

      Alert.alert(
        "Success",
        isSchoolOnly
          ? "Opportunity created for your school!"
          : "Opportunity created and added to the wider BC STEM database!"
      );
      setShowCreateModal(false);
      setCreateForm({
        title: "",
        description: "",
        organization: "",
        type: "Program",
        location: "",
        website: "",
        eligibility: "",
        cost: "",
        startDate: "",
        endDate: "",
        applicationCloseDate: "",
      });
      setIsSchoolOnly(true);
      loadDashboardData();
    } catch (error: any) {
      console.error("Error creating opportunity:", error);
      Alert.alert("Error", error.message || "Failed to create opportunity");
    } finally {
      setIsCreating(false);
    }
  };

  // Mobile/tablet warning screen
  if (!isDesktop) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.centeredMessage}>
          <View style={[styles.iconCircle, { backgroundColor: colors.primaryLight }]}>
            <Ionicons name="desktop-outline" size={32} color={colors.primary} />
          </View>
          <Text style={[styles.centeredTitle, { color: colors.text }]}>
            Desktop Required
          </Text>
          <Text style={[styles.centeredSubtitle, { color: colors.textSecondary }]}>
            The School Admin Dashboard is optimized for desktop screens.{'\n'}
            Please use a device with at least 1024px width.
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // Not logged in
  if (!currentUser) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.centeredMessage}>
          <View style={[styles.iconCircle, { backgroundColor: colors.primaryLight }]}>
            <Ionicons name="school-outline" size={32} color={colors.primary} />
          </View>
          <Text style={[styles.centeredTitle, { color: colors.text }]}>
            School Admin Dashboard
          </Text>
          <Text style={[styles.centeredSubtitle, { color: colors.textSecondary }]}>
            Sign in with your school administrator account to access your dashboard.
          </Text>
          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.primary }]}
            onPress={() => navigation.navigate('Login')}
          >
            <Ionicons name="log-in-outline" size={20} color="#FFFFFF" />
            <Text style={styles.primaryButtonText}>Sign In</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Not a school admin
  if (!isSchoolAdmin) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.centeredMessage}>
          <View style={[styles.iconCircle, { backgroundColor: colors.errorLight }]}>
            <Ionicons name="lock-closed-outline" size={32} color={colors.error} />
          </View>
          <Text style={[styles.centeredTitle, { color: colors.text }]}>
            Access Restricted
          </Text>
          <Text style={[styles.centeredSubtitle, { color: colors.textSecondary }]}>
            This dashboard is only available to school administrators.{'\n'}
            Current account: {currentUser.get('email')}
          </Text>
          <TouchableOpacity
            style={[styles.outlineButton, { borderColor: colors.border }]}
            onPress={() => navigation.navigate('Login')}
          >
            <Ionicons name="swap-horizontal-outline" size={20} color={colors.text} />
            <Text style={[styles.outlineButtonText, { color: colors.text }]}>Switch Account</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Error state
  if (error) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.centeredMessage}>
          <View style={[styles.iconCircle, { backgroundColor: colors.errorLight }]}>
            <Ionicons name="alert-circle-outline" size={32} color={colors.error} />
          </View>
          <Text style={[styles.centeredTitle, { color: colors.text }]}>
            Unable to Load
          </Text>
          <Text style={[styles.centeredSubtitle, { color: colors.textSecondary }]}>
            {error}
          </Text>
          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.primary }]}
            onPress={loadDashboardData}
          >
            <Ionicons name="refresh-outline" size={20} color="#FFFFFF" />
            <Text style={styles.primaryButtonText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Sidebar component
  const renderSidebar = () => (
    <Animated.View style={[
      styles.sidebar,
      sidebarCollapsed && styles.sidebarCollapsed,
      { backgroundColor: colors.sidebarBg, borderRightColor: colors.sidebarBorder, opacity: fadeAnim }
    ]}>
      {/* Sidebar Header */}
      <View style={[styles.sidebarHeader, { borderBottomColor: colors.border }]}>
        {!sidebarCollapsed && (
          <View style={styles.schoolBadge}>
            <View style={[styles.schoolAvatar, { backgroundColor: colors.primaryLight }]}>
              <Ionicons name="school" size={20} color={colors.primary} />
            </View>
            <View style={styles.schoolInfo}>
              <Text style={[styles.schoolName, { color: colors.text }]} numberOfLines={1}>
                {schoolName}
              </Text>
              <Text style={[styles.adminLabel, { color: colors.textMuted }]}>
                Admin Portal
              </Text>
            </View>
          </View>
        )}
        <TouchableOpacity
          onPress={() => setSidebarCollapsed(!sidebarCollapsed)}
          style={[styles.collapseBtn, { backgroundColor: colors.buttonBg }]}
        >
          <Ionicons
            name={sidebarCollapsed ? "chevron-forward" : "chevron-back"}
            size={18}
            color={colors.textSecondary}
          />
        </TouchableOpacity>
      </View>

      {/* Navigation */}
      <View style={styles.sidebarNav}>
        {[
          { id: 'overview' as DashboardSection, icon: 'grid-outline', label: 'Overview' },
          { id: 'opportunities' as DashboardSection, icon: 'folder-outline', label: 'Opportunities' },
          { id: 'browse' as DashboardSection, icon: 'search-outline', label: 'Browse & Add' },
          { id: 'settings' as DashboardSection, icon: 'settings-outline', label: 'Settings' },
        ].map((item) => {
          const isActive = activeSection === item.id;
          return (
            <TouchableOpacity
              key={item.id}
              style={[
                styles.navItem,
                isActive && { backgroundColor: colors.primaryLight },
                sidebarCollapsed && styles.navItemCollapsed,
              ]}
              onPress={() => setActiveSection(item.id)}
              activeOpacity={0.7}
            >
              <Ionicons
                name={item.icon as any}
                size={20}
                color={isActive ? colors.primary : colors.textSecondary}
              />
              {!sidebarCollapsed && (
                <Text style={[
                  styles.navLabel,
                  { color: isActive ? colors.primary : colors.textSecondary },
                  isActive && styles.navLabelActive,
                ]}>
                  {item.label}
                </Text>
              )}
              {isActive && !sidebarCollapsed && (
                <View style={[styles.navIndicator, { backgroundColor: colors.primary }]} />
              )}
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Student Portal Link */}
      {schoolSlug && (
        <View style={[styles.sidebarPortalSection, { borderTopColor: colors.border }]}>
          <TouchableOpacity
            style={[
              styles.portalButton,
              { backgroundColor: colors.successLight, borderColor: colors.success + '30' },
              sidebarCollapsed && styles.portalButtonCollapsed,
            ]}
            onPress={() => Linking.openURL(`https://bcstem.ca/${schoolSlug}`)}
          >
            <Ionicons name="people" size={18} color={colors.success} />
            {!sidebarCollapsed && (
              <Text style={[styles.portalButtonText, { color: colors.success }]}>
                Visit Student Portal
              </Text>
            )}
          </TouchableOpacity>
          {!sidebarCollapsed && (
            <Text style={[styles.portalUrl, { color: colors.textMuted }]}>
              bcstem.ca/{schoolSlug}
            </Text>
          )}
        </View>
      )}

      {/* Sidebar Footer */}
      <View style={[styles.sidebarFooter, { borderTopColor: colors.border }]}>
        <TouchableOpacity
          onPress={toggleTheme}
          style={[styles.themeBtn, { backgroundColor: colors.buttonBg }]}
        >
          <Animated.View
            style={{
              transform: [{
                rotate: themeIconRotation.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0deg', '180deg'],
                }),
              }],
            }}
          >
            <Ionicons
              name={theme === 'dark' ? 'sunny' : 'moon'}
              size={18}
              color={colors.textSecondary}
            />
          </Animated.View>
        </TouchableOpacity>
        {!sidebarCollapsed && (
          <View style={styles.userInfo}>
            <Text style={[styles.userName, { color: colors.text }]} numberOfLines={1}>
              {adminName}
            </Text>
            <Text style={[styles.userEmail, { color: colors.textMuted }]} numberOfLines={1}>
              {adminEmail}
            </Text>
          </View>
        )}
        <TouchableOpacity
          onPress={() => {
            Alert.alert(
              'Sign Out',
              'Are you sure you want to sign out?',
              [
                { text: 'Cancel', style: 'cancel' },
                {
                  text: 'Sign Out',
                  style: 'destructive',
                  onPress: async () => {
                    await logout();
                    navigation.navigate('Home', {});
                  },
                },
              ]
            );
          }}
          style={[styles.logoutBtn, { backgroundColor: colors.errorLight }]}
        >
          <Ionicons name="log-out-outline" size={18} color={colors.error} />
        </TouchableOpacity>
      </View>
    </Animated.View>
  );

  // Overview section
  const renderOverview = () => (
    <ScrollView style={styles.mainScroll} showsVerticalScrollIndicator={false}>
      <Animated.View style={[styles.contentContainer, { opacity: fadeAnim }]}>
        {/* Welcome Header */}
        <View style={styles.welcomeSection}>
          <View>
            <Text style={[styles.greeting, { color: colors.textSecondary }]}>
              Welcome back,
            </Text>
            <Text style={[styles.pageTitle, { color: colors.text }]}>
              {schoolName}
            </Text>
          </View>
          {schoolSlug && (
            <TouchableOpacity
              style={[styles.portalLink, { backgroundColor: colors.primaryLight, borderColor: colors.primary }]}
              onPress={() => navigation.navigate('StudentPortal', { schoolSlug })}
            >
              <Ionicons name="open-outline" size={16} color={colors.primary} />
              <Text style={[styles.portalLinkText, { color: colors.primary }]}>
                View Student Portal
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Stats Grid */}
        <View style={styles.statsGrid}>
          <View style={[styles.statCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={[styles.statIconWrap, { backgroundColor: colors.primaryLight }]}>
              <Ionicons name="folder-open" size={22} color={colors.primary} />
            </View>
            <View style={styles.statContent}>
              <Text style={[styles.statValue, { color: colors.text }]}>
                {dashboardStats.totalOpportunities}
              </Text>
              <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
                Total Opportunities
              </Text>
            </View>
          </View>

          <View style={[styles.statCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={[styles.statIconWrap, { backgroundColor: colors.successLight }]}>
              <Ionicons name="eye" size={22} color={colors.success} />
            </View>
            <View style={styles.statContent}>
              <Text style={[styles.statValue, { color: colors.text }]}>
                {dashboardStats.activeOpportunities}
              </Text>
              <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
                Visible to Students
              </Text>
            </View>
          </View>

          <View style={[styles.statCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={[styles.statIconWrap, { backgroundColor: colors.purpleLight }]}>
              <Ionicons name="people" size={22} color={colors.purple} />
            </View>
            <View style={styles.statContent}>
              <Text style={[styles.statValue, { color: colors.text }]}>
                {dashboardStats.totalTeacherSignups}
              </Text>
              <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
                Teacher Signups
              </Text>
            </View>
          </View>
        </View>

        {/* Recent Opportunities */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>Recent Opportunities</Text>
            {opportunities.length > 0 && (
              <TouchableOpacity onPress={() => setActiveSection('opportunities')}>
                <Text style={[styles.viewAllLink, { color: colors.primary }]}>View All</Text>
              </TouchableOpacity>
            )}
          </View>
          
          {isLoading ? (
            <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: 40 }} />
          ) : opportunities.length === 0 ? (
            <View style={[styles.emptyCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <View style={[styles.emptyIconWrap, { backgroundColor: colors.primaryLight }]}>
                <Ionicons name="folder-open-outline" size={32} color={colors.primary} />
              </View>
              <Text style={[styles.emptyTitle, { color: colors.text }]}>No opportunities yet</Text>
              <Text style={[styles.emptyDesc, { color: colors.textSecondary }]}>
                Start by browsing and adding opportunities from our database.
              </Text>
              <TouchableOpacity
                style={[styles.primaryButton, { backgroundColor: colors.primary, marginTop: 16 }]}
                onPress={() => setActiveSection('browse')}
              >
                <Ionicons name="add" size={20} color="#FFFFFF" />
                <Text style={styles.primaryButtonText}>Browse Opportunities</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.opportunitiesPreview}>
              {opportunities.slice(0, 3).map((item) => (
                <TouchableOpacity
                  key={item.schoolOpportunity.id}
                  style={[styles.opportunityRow, { backgroundColor: colors.card, borderColor: colors.border }]}
                  onPress={() => handleViewOpportunity(item)}
                  activeOpacity={0.7}
                >
                  <View style={styles.opportunityRowContent}>
                    <Text style={[styles.opportunityRowTitle, { color: colors.text }]} numberOfLines={1}>
                      {item.opportunity.get('title')}
                    </Text>
                    <Text style={[styles.opportunityRowOrg, { color: colors.textSecondary }]} numberOfLines={1}>
                      {item.opportunity.get('organization')}
                    </Text>
                  </View>
                  <View style={styles.opportunityRowMeta}>
                    {item.teacherCount > 0 && (
                      <View style={[styles.teacherBadge, { backgroundColor: colors.purpleLight }]}>
                        <Ionicons name="people" size={12} color={colors.purple} />
                        <Text style={[styles.teacherBadgeText, { color: colors.purple }]}>
                          {item.teacherCount}
                        </Text>
                      </View>
                    )}
                    {!item.schoolOpportunity.get('isVisible') && (
                      <View style={[styles.hiddenBadge, { backgroundColor: colors.warningLight }]}>
                        <Ionicons name="eye-off" size={12} color={colors.warning} />
                      </View>
                    )}
                    <Ionicons name="chevron-forward" size={18} color={colors.textMuted} />
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>

        {/* Getting Started Guide */}
        {opportunities.length < 3 && (
          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>Getting Started</Text>
            <View style={[styles.guideCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              {[
                { step: 1, title: 'Browse opportunities', desc: 'Search our database for STEM opportunities', done: opportunities.length > 0 },
                { step: 2, title: 'Add to your school', desc: 'Curate the best opportunities for your students', done: opportunities.length > 0 },
                { step: 3, title: 'Teachers sign up', desc: 'Teachers indicate which opportunities they can mentor', done: dashboardStats.totalTeacherSignups > 0 },
                { step: 4, title: 'Share with students', desc: 'Students see opportunities with teacher support', done: false },
              ].map((item, index) => (
                <View key={item.step} style={[styles.guideStep, index < 3 && { borderBottomWidth: 1, borderBottomColor: colors.borderLight }]}>
                  <View style={[
                    styles.guideStepNumber,
                    { backgroundColor: item.done ? colors.success : colors.buttonBg }
                  ]}>
                    {item.done ? (
                      <Ionicons name="checkmark" size={14} color="#FFFFFF" />
                    ) : (
                      <Text style={[styles.guideStepNumberText, { color: item.done ? '#FFFFFF' : colors.textSecondary }]}>
                        {item.step}
                      </Text>
                    )}
                  </View>
                  <View style={styles.guideStepContent}>
                    <Text style={[styles.guideStepTitle, { color: colors.text }]}>{item.title}</Text>
                    <Text style={[styles.guideStepDesc, { color: colors.textSecondary }]}>{item.desc}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}
      </Animated.View>
    </ScrollView>
  );

  // Opportunities section
  const renderOpportunities = () => (
    <ScrollView style={styles.mainScroll} showsVerticalScrollIndicator={false}>
      <View style={styles.contentContainer}>
        <View style={styles.pageHeader}>
          <View>
            <Text style={[styles.pageTitle, { color: colors.text }]}>Opportunities</Text>
            <Text style={[styles.pageSubtitle, { color: colors.textSecondary }]}>
              Manage your school's opportunity list
            </Text>
          </View>
          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.primary }]}
            onPress={() => setActiveSection('browse')}
          >
            <Ionicons name="add" size={20} color="#FFFFFF" />
            <Text style={styles.primaryButtonText}>Add New</Text>
          </TouchableOpacity>
        </View>

        {isLoading ? (
          <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: 60 }} />
        ) : opportunities.length === 0 ? (
          <View style={[styles.emptyCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={[styles.emptyIconWrap, { backgroundColor: colors.primaryLight }]}>
              <Ionicons name="folder-open-outline" size={32} color={colors.primary} />
            </View>
            <Text style={[styles.emptyTitle, { color: colors.text }]}>No opportunities yet</Text>
            <Text style={[styles.emptyDesc, { color: colors.textSecondary }]}>
              Browse our database and add opportunities for your students.
            </Text>
            <TouchableOpacity
              style={[styles.primaryButton, { backgroundColor: colors.primary, marginTop: 16 }]}
              onPress={() => setActiveSection('browse')}
            >
              <Ionicons name="search" size={20} color="#FFFFFF" />
              <Text style={styles.primaryButtonText}>Browse Database</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.opportunitiesList}>
            {opportunities.map((item) => (
              <TouchableOpacity
                key={item.schoolOpportunity.id}
                style={[styles.opportunityCard, { backgroundColor: colors.card, borderColor: colors.border }]}
                onPress={() => handleViewOpportunity(item)}
                activeOpacity={0.7}
              >
                <View style={styles.opportunityCardHeader}>
                  <View style={styles.opportunityCardTitleWrap}>
                    <Text style={[styles.opportunityCardTitle, { color: colors.text }]} numberOfLines={2}>
                      {item.opportunity.get('title')}
                    </Text>
                    {!item.schoolOpportunity.get('isVisible') && (
                      <View style={[styles.statusPill, { backgroundColor: colors.warningLight }]}>
                        <Ionicons name="eye-off" size={12} color={colors.warning} />
                        <Text style={[styles.statusPillText, { color: colors.warning }]}>Hidden</Text>
                      </View>
                    )}
                  </View>
                  <View style={styles.opportunityCardActions}>
                    <TouchableOpacity
                      onPress={(e) => {
                        e.stopPropagation();
                        handleToggleVisibility(
                          item.schoolOpportunity.id,
                          item.schoolOpportunity.get('isVisible')
                        );
                      }}
                      style={[
                        styles.visibilityToggle,
                        {
                          backgroundColor: item.schoolOpportunity.get('isVisible') ? colors.successLight : colors.buttonBg,
                          borderColor: item.schoolOpportunity.get('isVisible') ? colors.success + '40' : colors.border,
                        },
                      ]}
                    >
                      <Ionicons
                        name={item.schoolOpportunity.get('isVisible') ? 'eye' : 'eye-off'}
                        size={14}
                        color={item.schoolOpportunity.get('isVisible') ? colors.success : colors.textMuted}
                      />
                      <Text style={[
                        styles.visibilityToggleText,
                        { color: item.schoolOpportunity.get('isVisible') ? colors.success : colors.textMuted },
                      ]}>
                        {item.schoolOpportunity.get('isVisible') ? 'Visible' : 'Hidden'}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={(e) => {
                        e.stopPropagation();
                        handleRemoveOpportunity(item.schoolOpportunity.id);
                      }}
                      style={[styles.iconBtn, { backgroundColor: colors.errorLight }]}
                    >
                      <Ionicons name="trash-outline" size={18} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                </View>
                
                <Text style={[styles.opportunityCardDesc, { color: colors.textSecondary }]} numberOfLines={2}>
                  {item.opportunity.get('description')}
                </Text>
                
                <View style={styles.opportunityCardFooter}>
                  <View style={styles.opportunityCardMeta}>
                    <Ionicons name="business-outline" size={14} color={colors.textMuted} />
                    <Text style={[styles.opportunityCardMetaText, { color: colors.textMuted }]}>
                      {item.opportunity.get('organization')}
                    </Text>
                  </View>
                  <TouchableOpacity
                    onPress={(e) => {
                      e.stopPropagation();
                      if (item.teacherCount === 0) return;
                      setExpandedOpps(prev => {
                        const next = new Set(prev);
                        if (next.has(item.schoolOpportunity.id)) {
                          next.delete(item.schoolOpportunity.id);
                        } else {
                          next.add(item.schoolOpportunity.id);
                        }
                        return next;
                      });
                    }}
                    style={[styles.teacherBadgeLarge, { backgroundColor: item.teacherCount > 0 ? colors.purpleLight : colors.buttonBg }]}
                    activeOpacity={item.teacherCount > 0 ? 0.7 : 1}
                  >
                    <Ionicons name="people" size={14} color={item.teacherCount > 0 ? colors.purple : colors.textMuted} />
                    <Text style={[styles.teacherBadgeLargeText, { color: item.teacherCount > 0 ? colors.purple : colors.textMuted }]}>
                      {item.teacherCount > 0
                        ? `${item.teacherCount} ${item.teacherCount === 1 ? 'teacher' : 'teachers'}`
                        : 'No teachers yet'}
                    </Text>
                    {item.teacherCount > 0 && (
                      <Ionicons
                        name={expandedOpps.has(item.schoolOpportunity.id) ? 'chevron-up' : 'chevron-down'}
                        size={14}
                        color={colors.purple}
                      />
                    )}
                  </TouchableOpacity>
                </View>

                {/* Expandable teacher details */}
                {expandedOpps.has(item.schoolOpportunity.id) && item.teachers.length > 0 && (
                  <View style={[styles.teacherExpandedList, { borderTopColor: colors.borderLight }]}>
                    {item.teachers.map((teacher, idx) => (
                      <View key={idx} style={styles.teacherExpandedRow}>
                        <Ionicons name="person-outline" size={14} color={colors.textSecondary} />
                        <Text style={[styles.teacherExpandedName, { color: colors.text }]}>
                          {teacher.name}
                        </Text>
                        {teacher.roomNumber && (
                          <Text style={[styles.teacherExpandedRoom, { color: colors.textMuted }]}>
                            Room {teacher.roomNumber}
                          </Text>
                        )}
                      </View>
                    ))}
                  </View>
                )}
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>
    </ScrollView>
  );

  // Browse section
  const renderBrowse = () => (
    <ScrollView style={styles.mainScroll} showsVerticalScrollIndicator={false}>
      <View style={styles.contentContainer}>
        <View style={styles.pageHeader}>
          <View style={{ flex: 1 }}>
            <Text style={[styles.pageTitle, { color: colors.text }]}>Browse & Add</Text>
            <Text style={[styles.pageSubtitle, { color: colors.textSecondary }]}>
              Search our database or create a new opportunity
            </Text>
          </View>
          <TouchableOpacity
            style={[styles.createNewBtn, { backgroundColor: colors.primary }]}
            onPress={() => setShowCreateModal(true)}
          >
            <Ionicons name="add" size={20} color="#FFFFFF" />
            <Text style={styles.createNewBtnText}>Create New</Text>
          </TouchableOpacity>
        </View>

        {/* Search Bar */}
        <View style={[styles.searchContainer, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <Ionicons name="search" size={20} color={colors.textMuted} />
          <TextInput
            style={[styles.searchInput, { color: colors.text }]}
            placeholder="Search opportunities by name, organization, or type..."
            placeholderTextColor={colors.textMuted}
            value={searchTerm}
            onChangeText={setSearchTerm}
            onSubmitEditing={handleSearch}
          />
          {searchTerm.length > 0 && (
            <TouchableOpacity onPress={() => { setSearchTerm(''); setSearchResults([]); }}>
              <Ionicons name="close-circle" size={20} color={colors.textMuted} />
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={[styles.searchBtn, { backgroundColor: colors.primary }]}
            onPress={handleSearch}
          >
            <Text style={styles.searchBtnText}>Search</Text>
          </TouchableOpacity>
        </View>

        {/* Results */}
        {isSearching ? (
          <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: 60 }} />
        ) : searchResults.length > 0 ? (
          <View style={styles.searchResults}>
            <Text style={[styles.resultsCount, { color: colors.textSecondary }]}>
              Found {searchResults.length} opportunities
            </Text>
            {searchResults.map((opp) => (
              <View key={opp.id} style={[styles.searchResultCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={styles.searchResultContent}>
                  <Text style={[styles.searchResultTitle, { color: colors.text }]} numberOfLines={1}>
                    {opp.get('title')}
                  </Text>
                  <Text style={[styles.searchResultDesc, { color: colors.textSecondary }]} numberOfLines={2}>
                    {opp.get('description')}
                  </Text>
                  <View style={styles.searchResultMeta}>
                    <Text style={[styles.searchResultMetaText, { color: colors.textMuted }]}>
                      {opp.get('organization')}
                    </Text>
                    <View style={[styles.typePill, { backgroundColor: colors.primaryLight }]}>
                      <Text style={[styles.typePillText, { color: colors.primary }]}>
                        {opp.get('type')}
                      </Text>
                    </View>
                  </View>
                </View>
                <TouchableOpacity
                  style={[styles.addBtn, { backgroundColor: colors.success }]}
                  onPress={() => handleAddOpportunity(opp.id)}
                >
                  <Ionicons name="add" size={20} color="#FFFFFF" />
                  <Text style={styles.addBtnText}>Add</Text>
                </TouchableOpacity>
              </View>
            ))}
          </View>
        ) : searchTerm && !isSearching ? (
          <View style={[styles.emptyCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={[styles.emptyIconWrap, { backgroundColor: colors.warningLight }]}>
              <Ionicons name="search-outline" size={32} color={colors.warning} />
            </View>
            <Text style={[styles.emptyTitle, { color: colors.text }]}>No results found</Text>
            <Text style={[styles.emptyDesc, { color: colors.textSecondary }]}>
              Try different keywords or check your spelling.
            </Text>
          </View>
        ) : (
          <View style={[styles.emptyCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={[styles.emptyIconWrap, { backgroundColor: colors.primaryLight }]}>
              <Ionicons name="search-outline" size={32} color={colors.primary} />
            </View>
            <Text style={[styles.emptyTitle, { color: colors.text }]}>Search our database</Text>
            <Text style={[styles.emptyDesc, { color: colors.textSecondary }]}>
              Enter a keyword above to find opportunities to add to your school.
            </Text>
          </View>
        )}
      </View>
    </ScrollView>
  );

  // Settings section
  const renderSettings = () => (
    <ScrollView style={styles.mainScroll} showsVerticalScrollIndicator={false}>
      <View style={styles.contentContainer}>
        <View style={styles.pageHeader}>
          <View>
            <Text style={[styles.pageTitle, { color: colors.text }]}>Settings</Text>
            <Text style={[styles.pageSubtitle, { color: colors.textSecondary }]}>
              School profile and portal settings
            </Text>
          </View>
        </View>

        {/* School Profile Card */}
        <View style={[styles.settingsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={styles.settingsCardHeader}>
            <View style={[styles.schoolAvatarLarge, { backgroundColor: colors.primaryLight }]}>
              <Ionicons name="school" size={32} color={colors.primary} />
            </View>
            <View style={styles.settingsCardHeaderText}>
              <Text style={[styles.settingsCardTitle, { color: colors.text }]}>{schoolName}</Text>
              {schoolSlug && (
                <Text style={[styles.settingsCardSubtitle, { color: colors.textSecondary }]}>
                  bcstem.ca/{schoolSlug}
                </Text>
              )}
            </View>
          </View>

          <View style={[styles.settingsDivider, { backgroundColor: colors.borderLight }]} />

          <View style={styles.settingsRow}>
            <View style={styles.settingsRowIcon}>
              <Ionicons name="person-outline" size={20} color={colors.textSecondary} />
            </View>
            <View style={styles.settingsRowContent}>
              <Text style={[styles.settingsRowLabel, { color: colors.textMuted }]}>Administrator</Text>
              <Text style={[styles.settingsRowValue, { color: colors.text }]}>{adminEmail}</Text>
            </View>
          </View>

          {schoolWebsite && (
            <TouchableOpacity
              style={styles.settingsRow}
              onPress={() => Linking.openURL(schoolWebsite)}
            >
              <View style={styles.settingsRowIcon}>
                <Ionicons name="globe-outline" size={20} color={colors.textSecondary} />
              </View>
              <View style={styles.settingsRowContent}>
                <Text style={[styles.settingsRowLabel, { color: colors.textMuted }]}>School Website</Text>
                <Text style={[styles.settingsRowValue, { color: colors.primary }]}>{schoolWebsite}</Text>
              </View>
              <Ionicons name="open-outline" size={16} color={colors.textMuted} />
            </TouchableOpacity>
          )}

          {schoolSlug && (
            <TouchableOpacity
              style={styles.settingsRow}
              onPress={() => navigation.navigate('StudentPortal', { schoolSlug })}
            >
              <View style={styles.settingsRowIcon}>
                <Ionicons name="people-outline" size={20} color={colors.textSecondary} />
              </View>
              <View style={styles.settingsRowContent}>
                <Text style={[styles.settingsRowLabel, { color: colors.textMuted }]}>Student Portal</Text>
                <Text style={[styles.settingsRowValue, { color: colors.primary }]}>View public portal</Text>
              </View>
              <Ionicons name="open-outline" size={16} color={colors.textMuted} />
            </TouchableOpacity>
          )}
        </View>

        {/* Stats Summary */}
        <View style={[styles.settingsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <Text style={[styles.settingsCardSectionTitle, { color: colors.text }]}>Statistics</Text>
          <View style={styles.settingsStatsRow}>
            <View style={styles.settingsStatItem}>
              <Text style={[styles.settingsStatValue, { color: colors.primary }]}>
                {dashboardStats.totalOpportunities}
              </Text>
              <Text style={[styles.settingsStatLabel, { color: colors.textSecondary }]}>Opportunities</Text>
            </View>
            <View style={[styles.settingsStatDivider, { backgroundColor: colors.borderLight }]} />
            <View style={styles.settingsStatItem}>
              <Text style={[styles.settingsStatValue, { color: colors.success }]}>
                {dashboardStats.activeOpportunities}
              </Text>
              <Text style={[styles.settingsStatLabel, { color: colors.textSecondary }]}>Visible</Text>
            </View>
            <View style={[styles.settingsStatDivider, { backgroundColor: colors.borderLight }]} />
            <View style={styles.settingsStatItem}>
              <Text style={[styles.settingsStatValue, { color: colors.purple }]}>
                {dashboardStats.totalTeacherSignups}
              </Text>
              <Text style={[styles.settingsStatLabel, { color: colors.textSecondary }]}>Teachers</Text>
            </View>
          </View>
        </View>
      </View>
    </ScrollView>
  );

  // Detail Modal
  const renderDetailModal = () => (
    <Modal
      visible={showDetailModal}
      animationType="fade"
      transparent={true}
      onRequestClose={() => setShowDetailModal(false)}
    >
      <TouchableWithoutFeedback onPress={() => setShowDetailModal(false)}>
        <View style={styles.modalOverlay}>
          <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
            <View style={[styles.modalCard, { backgroundColor: colors.card }]}>
          <View style={[styles.modalHeader, { borderBottomColor: colors.borderLight }]}>
            <Text style={[styles.modalTitle, { color: colors.text }]} numberOfLines={2}>
              {selectedOpportunity?.opportunity.get('title')}
            </Text>
            <TouchableOpacity
              onPress={() => setShowDetailModal(false)}
              style={[styles.modalCloseBtn, { backgroundColor: colors.buttonBg }]}
            >
              <Ionicons name="close" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
            {/* Status Badges */}
            <View style={styles.modalBadges}>
              <View style={[
                styles.modalBadge,
                { backgroundColor: selectedOpportunity?.schoolOpportunity.get('isVisible') ? colors.successLight : colors.warningLight }
              ]}>
                <Ionicons
                  name={selectedOpportunity?.schoolOpportunity.get('isVisible') ? 'eye' : 'eye-off'}
                  size={14}
                  color={selectedOpportunity?.schoolOpportunity.get('isVisible') ? colors.success : colors.warning}
                />
                <Text style={{
                  color: selectedOpportunity?.schoolOpportunity.get('isVisible') ? colors.success : colors.warning,
                  fontSize: 13,
                  fontWeight: '600',
                  marginLeft: 6,
                }}>
                  {selectedOpportunity?.schoolOpportunity.get('isVisible') ? 'Visible to Students' : 'Hidden'}
                </Text>
              </View>
              <View style={[styles.modalBadge, { backgroundColor: colors.purpleLight }]}>
                <Ionicons name="people" size={14} color={colors.purple} />
                <Text style={{ color: colors.purple, fontSize: 13, fontWeight: '600', marginLeft: 6 }}>
                  {selectedOpportunity?.teacherCount || 0} Teachers
                </Text>
              </View>
            </View>

            {/* Description */}
            <View style={styles.modalSection}>
              <Text style={[styles.modalSectionTitle, { color: colors.textMuted }]}>Description</Text>
              <Text style={[styles.modalSectionText, { color: colors.text }]}>
                {selectedOpportunity?.opportunity.get('description')}
              </Text>
            </View>

            {/* Details Grid */}
            <View style={styles.modalDetailsGrid}>
              <View style={styles.modalDetailItem}>
                <Text style={[styles.modalDetailLabel, { color: colors.textMuted }]}>Organization</Text>
                <Text style={[styles.modalDetailValue, { color: colors.text }]}>
                  {selectedOpportunity?.opportunity.get('organization')}
                </Text>
              </View>
              <View style={styles.modalDetailItem}>
                <Text style={[styles.modalDetailLabel, { color: colors.textMuted }]}>Type</Text>
                <Text style={[styles.modalDetailValue, { color: colors.text }]}>
                  {selectedOpportunity?.opportunity.get('type')}
                </Text>
              </View>
              <View style={styles.modalDetailItem}>
                <Text style={[styles.modalDetailLabel, { color: colors.textMuted }]}>Location</Text>
                <Text style={[styles.modalDetailValue, { color: colors.text }]}>
                  {selectedOpportunity?.opportunity.get('location') || 'Not specified'}
                </Text>
              </View>
              <View style={styles.modalDetailItem}>
                <Text style={[styles.modalDetailLabel, { color: colors.textMuted }]}>Start Date</Text>
                <Text style={[styles.modalDetailValue, { color: colors.text }]}>
                  {formatDate(selectedOpportunity?.opportunity.get('startDate'))}
                </Text>
              </View>
              <View style={styles.modalDetailItem}>
                <Text style={[styles.modalDetailLabel, { color: colors.textMuted }]}>End Date</Text>
                <Text style={[styles.modalDetailValue, { color: colors.text }]}>
                  {formatDate(selectedOpportunity?.opportunity.get('endDate'))}
                </Text>
              </View>
              <View style={styles.modalDetailItem}>
                <Text style={[styles.modalDetailLabel, { color: colors.textMuted }]}>Deadline</Text>
                <Text style={[styles.modalDetailValue, { color: colors.text }]}>
                  {formatDate(selectedOpportunity?.opportunity.get('applicationCloseDate'))}
                </Text>
              </View>
            </View>

            {/* Website Link */}
            {selectedOpportunity?.opportunity.get('website') && (
              <TouchableOpacity
                style={[styles.modalWebsiteBtn, { borderColor: colors.primary }]}
                onPress={() => Linking.openURL(selectedOpportunity.opportunity.get('website'))}
              >
                <Ionicons name="link-outline" size={18} color={colors.primary} />
                <Text style={[styles.modalWebsiteBtnText, { color: colors.primary }]}>
                  Visit Opportunity Website
                </Text>
              </TouchableOpacity>
            )}
          </ScrollView>

          {/* Modal Footer */}
          <View style={[styles.modalFooter, { borderTopColor: colors.borderLight }]}>
            <TouchableOpacity
              style={[styles.modalActionBtn, { backgroundColor: colors.buttonBg }]}
              onPress={() => {
                if (selectedOpportunity) {
                  handleToggleVisibility(
                    selectedOpportunity.schoolOpportunity.id,
                    selectedOpportunity.schoolOpportunity.get('isVisible')
                  );
                  setShowDetailModal(false);
                }
              }}
            >
              <Ionicons
                name={selectedOpportunity?.schoolOpportunity.get('isVisible') ? 'eye-off-outline' : 'eye-outline'}
                size={18}
                color={colors.text}
              />
              <Text style={[styles.modalActionBtnText, { color: colors.text }]}>
                {selectedOpportunity?.schoolOpportunity.get('isVisible') ? 'Hide' : 'Show'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalActionBtn, { backgroundColor: colors.errorLight }]}
              onPress={() => {
                if (selectedOpportunity) {
                  setShowDetailModal(false);
                  handleRemoveOpportunity(selectedOpportunity.schoolOpportunity.id);
                }
              }}
            >
              <Ionicons name="trash-outline" size={18} color={colors.error} />
              <Text style={[styles.modalActionBtnText, { color: colors.error }]}>Remove</Text>
            </TouchableOpacity>
          </View>
        </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );

  const renderCreateModal = () => (
    <Modal
      visible={showCreateModal}
      animationType="fade"
      transparent={true}
      onRequestClose={() => setShowCreateModal(false)}
    >
      <TouchableWithoutFeedback onPress={() => setShowCreateModal(false)}>
        <View style={[styles.modalOverlay, { backgroundColor: 'rgba(0,0,0,0.5)' }]}>
          <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
            <View style={[styles.createModalContent, { backgroundColor: colors.card }]}>
              <View style={[styles.modalHeader, { borderBottomColor: colors.border }]}>
                <Text style={[styles.modalHeaderTitle, { color: colors.text }]}>
                  Create New Opportunity
                </Text>
                <TouchableOpacity onPress={() => setShowCreateModal(false)}>
                  <Ionicons name="close" size={24} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>

              <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
                {/* Visibility Toggle */}
                <View style={[styles.visibilitySection, { backgroundColor: colors.buttonBg, borderColor: colors.border }]}>
                  <Text style={[styles.visibilityTitle, { color: colors.text }]}>Visibility</Text>
                  <View style={styles.visibilityOptions}>
                    <TouchableOpacity
                      style={[
                        styles.visibilityOption,
                        {
                          backgroundColor: isSchoolOnly ? colors.primary : colors.card,
                          borderColor: isSchoolOnly ? colors.primary : colors.border,
                        },
                      ]}
                      onPress={() => setIsSchoolOnly(true)}
                    >
                      <Ionicons name="school-outline" size={18} color={isSchoolOnly ? "#fff" : colors.textSecondary} />
                      <Text style={[styles.visibilityOptionText, { color: isSchoolOnly ? "#fff" : colors.text }]}>
                        {schoolName} Only
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.visibilityOption,
                        {
                          backgroundColor: !isSchoolOnly ? colors.primary : colors.card,
                          borderColor: !isSchoolOnly ? colors.primary : colors.border,
                        },
                      ]}
                      onPress={() => setIsSchoolOnly(false)}
                    >
                      <Ionicons name="globe-outline" size={18} color={!isSchoolOnly ? "#fff" : colors.textSecondary} />
                      <Text style={[styles.visibilityOptionText, { color: !isSchoolOnly ? "#fff" : colors.text }]}>
                        BC STEM Database
                      </Text>
                    </TouchableOpacity>
                  </View>
                  <Text style={[styles.visibilityHint, { color: colors.textMuted }]}>
                    {isSchoolOnly
                      ? "Only visible to students and teachers at your school"
                      : "Visible to everyone on bcstem.ca + your school portal"}
                  </Text>
                </View>

                {/* Form Fields */}
                <View style={styles.createFormGroup}>
                  <Text style={[styles.createFormLabel, { color: colors.text }]}>
                    Title <Text style={{ color: colors.error }}>*</Text>
                  </Text>
                  <TextInput
                    style={[styles.createFormInput, { backgroundColor: colors.buttonBg, color: colors.text, borderColor: colors.border }]}
                    placeholder="e.g., Summer Science Research Program"
                    placeholderTextColor={colors.textMuted}
                    value={createForm.title}
                    onChangeText={(text) => setCreateForm({ ...createForm, title: text })}
                  />
                </View>

                <View style={styles.createFormGroup}>
                  <Text style={[styles.createFormLabel, { color: colors.text }]}>
                    Organization <Text style={{ color: colors.error }}>*</Text>
                  </Text>
                  <TextInput
                    style={[styles.createFormInput, { backgroundColor: colors.buttonBg, color: colors.text, borderColor: colors.border }]}
                    placeholder="e.g., UBC, SFU, or your school name"
                    placeholderTextColor={colors.textMuted}
                    value={createForm.organization}
                    onChangeText={(text) => setCreateForm({ ...createForm, organization: text })}
                  />
                </View>

                <View style={styles.createFormGroup}>
                  <Text style={[styles.createFormLabel, { color: colors.text }]}>
                    Description <Text style={{ color: colors.error }}>*</Text>
                  </Text>
                  <TextInput
                    style={[styles.createFormTextarea, { backgroundColor: colors.buttonBg, color: colors.text, borderColor: colors.border }]}
                    placeholder="Describe the opportunity..."
                    placeholderTextColor={colors.textMuted}
                    value={createForm.description}
                    onChangeText={(text) => setCreateForm({ ...createForm, description: text })}
                    multiline
                    numberOfLines={4}
                  />
                </View>

                <View style={styles.createFormGroup}>
                  <Text style={[styles.createFormLabel, { color: colors.text }]}>Type</Text>
                  <View style={styles.typeOptions}>
                    {["Program", "Competition", "Workshop", "Scholarship", "Internship", "Other"].map((type) => (
                      <TouchableOpacity
                        key={type}
                        style={[
                          styles.typeOption,
                          {
                            backgroundColor: createForm.type === type ? colors.primaryLight : colors.buttonBg,
                            borderColor: createForm.type === type ? colors.primary : colors.border,
                          },
                        ]}
                        onPress={() => setCreateForm({ ...createForm, type })}
                      >
                        <Text style={[styles.typeOptionText, { color: createForm.type === type ? colors.primary : colors.text }]}>
                          {type}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                <View style={styles.createFormRow}>
                  <View style={[styles.createFormGroup, { flex: 1 }]}>
                    <Text style={[styles.createFormLabel, { color: colors.text }]}>Location</Text>
                    <TextInput
                      style={[styles.createFormInput, { backgroundColor: colors.buttonBg, color: colors.text, borderColor: colors.border }]}
                      placeholder="Vancouver, BC"
                      placeholderTextColor={colors.textMuted}
                      value={createForm.location}
                      onChangeText={(text) => setCreateForm({ ...createForm, location: text })}
                    />
                  </View>
                  <View style={[styles.createFormGroup, { flex: 1 }]}>
                    <Text style={[styles.createFormLabel, { color: colors.text }]}>Cost</Text>
                    <TextInput
                      style={[styles.createFormInput, { backgroundColor: colors.buttonBg, color: colors.text, borderColor: colors.border }]}
                      placeholder="Free, $50, etc."
                      placeholderTextColor={colors.textMuted}
                      value={createForm.cost}
                      onChangeText={(text) => setCreateForm({ ...createForm, cost: text })}
                    />
                  </View>
                </View>

                <View style={styles.createFormGroup}>
                  <Text style={[styles.createFormLabel, { color: colors.text }]}>Website</Text>
                  <TextInput
                    style={[styles.createFormInput, { backgroundColor: colors.buttonBg, color: colors.text, borderColor: colors.border }]}
                    placeholder="https://..."
                    placeholderTextColor={colors.textMuted}
                    value={createForm.website}
                    onChangeText={(text) => setCreateForm({ ...createForm, website: text })}
                    autoCapitalize="none"
                  />
                </View>

                <View style={styles.createFormGroup}>
                  <Text style={[styles.createFormLabel, { color: colors.text }]}>Eligibility</Text>
                  <TextInput
                    style={[styles.createFormInput, { backgroundColor: colors.buttonBg, color: colors.text, borderColor: colors.border }]}
                    placeholder="e.g., Grades 10-12, BC residents"
                    placeholderTextColor={colors.textMuted}
                    value={createForm.eligibility}
                    onChangeText={(text) => setCreateForm({ ...createForm, eligibility: text })}
                  />
                </View>

                <View style={styles.createFormRow}>
                  <View style={[styles.createFormGroup, { flex: 1 }]}>
                    <Text style={[styles.createFormLabel, { color: colors.text }]}>Application Deadline</Text>
                    <TextInput
                      style={[styles.createFormInput, { backgroundColor: colors.buttonBg, color: colors.text, borderColor: colors.border }]}
                      placeholder="YYYY-MM-DD"
                      placeholderTextColor={colors.textMuted}
                      value={createForm.applicationCloseDate}
                      onChangeText={(text) => setCreateForm({ ...createForm, applicationCloseDate: text })}
                    />
                  </View>
                </View>

                <View style={styles.createFormRow}>
                  <View style={[styles.createFormGroup, { flex: 1 }]}>
                    <Text style={[styles.createFormLabel, { color: colors.text }]}>Start Date</Text>
                    <TextInput
                      style={[styles.createFormInput, { backgroundColor: colors.buttonBg, color: colors.text, borderColor: colors.border }]}
                      placeholder="YYYY-MM-DD"
                      placeholderTextColor={colors.textMuted}
                      value={createForm.startDate}
                      onChangeText={(text) => setCreateForm({ ...createForm, startDate: text })}
                    />
                  </View>
                  <View style={[styles.createFormGroup, { flex: 1 }]}>
                    <Text style={[styles.createFormLabel, { color: colors.text }]}>End Date</Text>
                    <TextInput
                      style={[styles.createFormInput, { backgroundColor: colors.buttonBg, color: colors.text, borderColor: colors.border }]}
                      placeholder="YYYY-MM-DD"
                      placeholderTextColor={colors.textMuted}
                      value={createForm.endDate}
                      onChangeText={(text) => setCreateForm({ ...createForm, endDate: text })}
                    />
                  </View>
                </View>
              </ScrollView>

              <View style={[styles.createModalFooter, { borderTopColor: colors.border }]}>
                <TouchableOpacity
                  style={[styles.createCancelBtn, { borderColor: colors.border }]}
                  onPress={() => setShowCreateModal(false)}
                  disabled={isCreating}
                >
                  <Text style={[styles.createCancelBtnText, { color: colors.text }]}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.createSubmitBtn, { backgroundColor: colors.primary }]}
                  onPress={handleCreateOpportunity}
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : (
                    <Text style={styles.createSubmitBtnText}>Create Opportunity</Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <Header />
      <View style={styles.dashboard}>
        {renderSidebar()}
        <View style={[styles.mainArea, { backgroundColor: colors.background }]}>
          {activeSection === 'overview' && renderOverview()}
          {activeSection === 'opportunities' && renderOpportunities()}
          {activeSection === 'browse' && renderBrowse()}
          {activeSection === 'settings' && renderSettings()}
        </View>
      </View>
      {renderDetailModal()}
      {renderCreateModal()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  dashboard: {
    flex: 1,
    flexDirection: 'row',
  },
  
  // Sidebar
  sidebar: {
    width: 260,
    borderRightWidth: 1,
    flexDirection: 'column',
  },
  sidebarCollapsed: {
    width: 72,
  },
  sidebarHeader: {
    padding: 20,
    borderBottomWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  schoolBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 12,
  },
  schoolAvatar: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  schoolInfo: {
    marginLeft: 12,
    flex: 1,
  },
  schoolName: {
    fontSize: 15,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  adminLabel: {
    fontSize: 12,
    marginTop: 2,
    fontFamily: 'Inter-Regular',
  },
  collapseBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sidebarNav: {
    flex: 1,
    paddingTop: 12,
    paddingHorizontal: 12,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    marginBottom: 4,
    position: 'relative',
  },
  navItemCollapsed: {
    justifyContent: 'center',
    paddingHorizontal: 0,
  },
  navLabel: {
    fontSize: 14,
    marginLeft: 12,
    fontFamily: 'Inter-Medium',
    fontWeight: '500',
  },
  navLabelActive: {
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  navIndicator: {
    position: 'absolute',
    right: 12,
    width: 4,
    height: 4,
    borderRadius: 2,
  },
  sidebarPortalSection: {
    padding: 12,
    borderTopWidth: 1,
  },
  portalButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  portalButtonCollapsed: {
    paddingHorizontal: 0,
  },
  portalButtonText: {
    fontSize: 13,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  portalUrl: {
    fontSize: 11,
    textAlign: 'center',
    marginTop: 6,
    fontFamily: 'Inter-Regular',
  },
  sidebarFooter: {
    padding: 12,
    borderTopWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  themeBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoutBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  userInfo: {
    marginLeft: 12,
    marginRight: 8,
    flex: 1,
  },
  userName: {
    fontSize: 13,
    fontWeight: '500',
    fontFamily: 'Inter-Medium',
  },
  userEmail: {
    fontSize: 11,
    marginTop: 2,
    fontFamily: 'Inter-Regular',
  },

  // Main Area
  mainArea: {
    flex: 1,
  },
  mainScroll: {
    flex: 1,
  },
  contentContainer: {
    padding: 32,
    maxWidth: 1000,
  },

  // Welcome Section
  welcomeSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 32,
  },
  greeting: {
    fontSize: 14,
    marginBottom: 4,
    fontFamily: 'Inter-Regular',
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    fontFamily: 'Inter-Bold',
  },
  pageSubtitle: {
    fontSize: 15,
    marginTop: 4,
    fontFamily: 'Inter-Regular',
  },
  portalLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  portalLinkText: {
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },

  // Page Header
  pageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
  },

  // Stats Grid
  statsGrid: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 32,
  },
  statCard: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    borderRadius: 14,
    borderWidth: 1,
  },
  statIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  statContent: {
    flex: 1,
  },
  statValue: {
    fontSize: 28,
    fontWeight: '700',
    fontFamily: 'Inter-Bold',
  },
  statLabel: {
    fontSize: 13,
    marginTop: 2,
    fontFamily: 'Inter-Regular',
  },

  // Sections
  section: {
    marginBottom: 32,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  viewAllLink: {
    fontSize: 14,
    fontWeight: '500',
    fontFamily: 'Inter-Medium',
  },

  // Opportunities Preview
  opportunitiesPreview: {
    gap: 8,
  },
  opportunityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  opportunityRowContent: {
    flex: 1,
    marginRight: 12,
  },
  opportunityRowTitle: {
    fontSize: 15,
    fontWeight: '500',
    marginBottom: 4,
    fontFamily: 'Inter-Medium',
  },
  opportunityRowOrg: {
    fontSize: 13,
    fontFamily: 'Inter-Regular',
  },
  opportunityRowMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  teacherBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    gap: 4,
  },
  teacherBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  hiddenBadge: {
    padding: 6,
    borderRadius: 6,
  },

  // Opportunities List
  opportunitiesList: {
    gap: 12,
  },
  opportunityCard: {
    padding: 20,
    borderRadius: 14,
    borderWidth: 1,
  },
  opportunityCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  opportunityCardTitleWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    marginRight: 12,
  },
  opportunityCardTitle: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
    fontFamily: 'Inter-SemiBold',
  },
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    gap: 4,
  },
  statusPillText: {
    fontSize: 11,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  opportunityCardActions: {
    flexDirection: 'row',
    gap: 8,
  },
  iconBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  opportunityCardDesc: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
    fontFamily: 'Inter-Regular',
  },
  opportunityCardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  opportunityCardMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  opportunityCardMetaText: {
    fontSize: 13,
    fontFamily: 'Inter-Regular',
  },
  teacherBadgeLarge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    gap: 6,
  },
  teacherBadgeLargeText: {
    fontSize: 13,
    fontWeight: '500',
    fontFamily: 'Inter-Medium',
  },

  // Visibility toggle
  visibilityToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    gap: 5,
  },
  visibilityToggleText: {
    fontSize: 12,
    fontWeight: '600',
  },

  // Expandable teacher list
  teacherExpandedList: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    gap: 8,
  },
  teacherExpandedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  teacherExpandedName: {
    fontSize: 13,
    fontWeight: '500',
    flex: 1,
  },
  teacherExpandedRoom: {
    fontSize: 12,
  },

  // Empty State
  emptyCard: {
    padding: 40,
    borderRadius: 14,
    borderWidth: 1,
    alignItems: 'center',
  },
  emptyIconWrap: {
    width: 64,
    height: 64,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 17,
    fontWeight: '600',
    marginBottom: 8,
    fontFamily: 'Inter-SemiBold',
  },
  emptyDesc: {
    fontSize: 14,
    textAlign: 'center',
    maxWidth: 300,
    lineHeight: 20,
    fontFamily: 'Inter-Regular',
  },

  // Guide Card
  guideCard: {
    borderRadius: 14,
    borderWidth: 1,
    overflow: 'hidden',
  },
  guideStep: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  guideStepNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  guideStepNumberText: {
    fontSize: 13,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  guideStepContent: {
    flex: 1,
  },
  guideStepTitle: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 2,
    fontFamily: 'Inter-Medium',
  },
  guideStepDesc: {
    fontSize: 13,
    fontFamily: 'Inter-Regular',
  },

  // Search
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 4,
    paddingLeft: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 24,
    gap: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    paddingVertical: 12,
    fontFamily: 'Inter-Regular',
  },
  searchBtn: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
  },
  searchBtnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  resultsCount: {
    fontSize: 14,
    marginBottom: 16,
    fontFamily: 'Inter-Regular',
  },
  searchResults: {
    gap: 12,
  },
  searchResultCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 16,
  },
  searchResultContent: {
    flex: 1,
  },
  searchResultTitle: {
    fontSize: 15,
    fontWeight: '500',
    marginBottom: 4,
    fontFamily: 'Inter-Medium',
  },
  searchResultDesc: {
    fontSize: 13,
    marginBottom: 8,
    lineHeight: 18,
    fontFamily: 'Inter-Regular',
  },
  searchResultMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  searchResultMetaText: {
    fontSize: 12,
    fontFamily: 'Inter-Regular',
  },
  typePill: {
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  typePillText: {
    fontSize: 11,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    gap: 6,
  },
  addBtnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },

  // Settings
  settingsCard: {
    borderRadius: 14,
    borderWidth: 1,
    marginBottom: 20,
    overflow: 'hidden',
  },
  settingsCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
  },
  schoolAvatarLarge: {
    width: 56,
    height: 56,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  settingsCardHeaderText: {
    marginLeft: 16,
    flex: 1,
  },
  settingsCardTitle: {
    fontSize: 18,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  settingsCardSubtitle: {
    fontSize: 14,
    marginTop: 4,
    fontFamily: 'Inter-Regular',
  },
  settingsCardSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    padding: 20,
    paddingBottom: 16,
    fontFamily: 'Inter-SemiBold',
  },
  settingsDivider: {
    height: 1,
  },
  settingsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    paddingHorizontal: 20,
  },
  settingsRowIcon: {
    width: 36,
    alignItems: 'center',
  },
  settingsRowContent: {
    flex: 1,
    marginLeft: 8,
  },
  settingsRowLabel: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
    fontFamily: 'Inter-Medium',
  },
  settingsRowValue: {
    fontSize: 15,
    fontFamily: 'Inter-Regular',
  },
  settingsStatsRow: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  settingsStatItem: {
    flex: 1,
    alignItems: 'center',
  },
  settingsStatDivider: {
    width: 1,
    height: '100%',
  },
  settingsStatValue: {
    fontSize: 28,
    fontWeight: '700',
    fontFamily: 'Inter-Bold',
  },
  settingsStatLabel: {
    fontSize: 13,
    marginTop: 4,
    fontFamily: 'Inter-Regular',
  },

  // Buttons
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    gap: 8,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  outlineButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  outlineButtonText: {
    fontSize: 15,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },

  // Centered Message (auth screens)
  centeredMessage: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  iconCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  centeredTitle: {
    fontSize: 22,
    fontWeight: '600',
    marginBottom: 12,
    textAlign: 'center',
    fontFamily: 'Inter-SemiBold',
  },
  centeredSubtitle: {
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
    maxWidth: 400,
    marginBottom: 24,
    fontFamily: 'Inter-Regular',
  },

  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalCard: {
    width: '100%',
    maxWidth: 560,
    maxHeight: '85%',
    borderRadius: 16,
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 20,
    borderBottomWidth: 1,
    gap: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    flex: 1,
    fontFamily: 'Inter-SemiBold',
  },
  modalCloseBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalBody: {
    padding: 20,
  },
  modalBadges: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 20,
  },
  modalBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  modalSection: {
    marginBottom: 20,
  },
  modalSectionTitle: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
    fontFamily: 'Inter-Medium',
  },
  modalSectionText: {
    fontSize: 15,
    lineHeight: 22,
    fontFamily: 'Inter-Regular',
  },
  modalDetailsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 20,
  },
  modalDetailItem: {
    width: '50%',
    marginBottom: 16,
  },
  modalDetailLabel: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
    fontFamily: 'Inter-Medium',
  },
  modalDetailValue: {
    fontSize: 14,
    fontFamily: 'Inter-Regular',
  },
  modalWebsiteBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  modalWebsiteBtnText: {
    fontSize: 15,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 16,
    borderTopWidth: 1,
    gap: 10,
  },
  modalActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    gap: 6,
  },
  modalActionBtnText: {
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  modalHeaderTitle: {
    fontSize: 18,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },

  // Create New Button
  createNewBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderRadius: 10,
    gap: 8,
  },
  createNewBtnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },

  // Create Modal
  createModalContent: {
    width: '100%',
    maxWidth: 640,
    maxHeight: '90%',
    borderRadius: 16,
    overflow: 'hidden',
  },
  visibilitySection: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 20,
  },
  visibilityTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 12,
    fontFamily: 'Inter-SemiBold',
  },
  visibilityOptions: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 8,
  },
  visibilityOption: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  visibilityOptionText: {
    fontSize: 13,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  visibilityHint: {
    fontSize: 12,
    textAlign: 'center',
    fontFamily: 'Inter-Regular',
  },
  createFormGroup: {
    marginBottom: 16,
  },
  createFormLabel: {
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 8,
    fontFamily: 'Inter-Medium',
  },
  createFormInput: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    fontFamily: 'Inter-Regular',
  },
  createFormTextarea: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    minHeight: 100,
    textAlignVertical: 'top',
    fontFamily: 'Inter-Regular',
  },
  createFormRow: {
    flexDirection: 'row',
    gap: 12,
  },
  typeOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  typeOption: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  typeOptionText: {
    fontSize: 13,
    fontWeight: '500',
    fontFamily: 'Inter-Medium',
  },
  createModalFooter: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    gap: 12,
  },
  createCancelBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
  },
  createCancelBtnText: {
    fontSize: 15,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
  createSubmitBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  createSubmitBtnText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '600',
    fontFamily: 'Inter-SemiBold',
  },
});

export default SchoolAdminDashboard;
